import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { getLandingRoute } from '@/lib/landingRoute';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ZoeaLogo from '@/components/ZoeaLogo';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Deze resetlink is ongeldig.');
      setLoading(false);
      return;
    }
    axios.get(`${API}/auth/reset-password/${token}`)
      .then((res) => setPreview(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Deze resetlink is niet (meer) geldig.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 karakters zijn');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }
    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/auth/reset-password`, { token, password });
      const projectedUser = await loginWithToken(response.data.access_token, response.data.user);
      toast.success('Je wachtwoord is gewijzigd. Je bent ingelogd.');
      navigate(getLandingRoute(projectedUser));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Wachtwoord resetten mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex flex-col justify-center px-4 sm:px-6">
      <div className="w-full max-w-sm mx-auto">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Terug naar inloggen</span>
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <ZoeaLogo className="h-12" />
        </div>

        {loading ? (
          <p className="text-slate-500">Link controleren...</p>
        ) : error ? (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2 font-['Manrope']">Link niet geldig</h1>
            <p className="text-slate-600 mb-8">{error}</p>
            <Link to="/forgot-password" className="text-teal-600 hover:text-teal-700 font-medium">
              Nieuwe resetlink aanvragen
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2 font-['Manrope']">Nieuw wachtwoord</h1>
            <p className="text-slate-600 mb-8">
              Stel een nieuw wachtwoord in voor <strong>{preview.email}</strong>.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Nieuw wachtwoord *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimaal 8 karakters"
                    className="h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Bevestig wachtwoord *</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Herhaal je wachtwoord"
                  className="h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium mt-2"
              >
                {submitting ? 'Bezig...' : 'Wachtwoord wijzigen en inloggen'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
